/**
 * Council Trail v0.1 Item 8 — useCouncilProposals.
 *
 * Hydrates pending proposals on mount + subscribes to councilWsClient for
 * PROPOSAL_QUEUED / PROPOSAL_RESOLVED so the list stays live as peers
 * propose + the operator resolves.
 *
 * Returns:
 *   - proposals: ProposalRow[]       (pending only — refreshes on each event)
 *   - loading: boolean               (initial hydrate)
 *   - error: Error | null            (last hydrate / resolve error)
 *   - resolve: (id, decision, note?) => Promise<void>
 *                                    (POST to resolve route; refreshes
 *                                     on success)
 *
 * Failure modes:
 *   - Initial fetch failure: error populated, proposals stays []
 *   - Resolve 409 (race): refresh + propagate error to caller (caller can
 *     toast "raced — refreshed")
 *   - WS events arrive while a hydrate is in flight: harmless — the next
 *     hydrate just runs again, no race-correctness concerns at this layer.
 */
import { useCallback, useEffect, useState } from 'react';

import { councilApiClient } from '../services/councilApiClient';
import { councilWsClient } from '../services/councilWsClient';
import type {
  PeerEventWire,
  ProposalRow,
  ResolveProposalInput,
} from '../types/council';

export interface UseCouncilProposalsResult {
  proposals: ProposalRow[];
  loading: boolean;
  error: Error | null;
  resolve: (proposalId: string, decision: 'approve' | 'dismiss', note?: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useCouncilProposals(sessionId: string): UseCouncilProposalsResult {
  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { proposals: rows } = await councilApiClient.listProposals(sessionId);
      setProposals(rows);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { proposals: rows } = await councilApiClient.listProposals(sessionId);
        if (cancelled) return;
        setProposals(rows);
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Live updates: any PROPOSAL_QUEUED or PROPOSAL_RESOLVED triggers a
  // refetch. Trade-off: simpler than reconciling individual events
  // into the local list (e.g., a partial-data target would require
  // backfilling the kind+rationale+target from a separate call); a
  // refetch is one round-trip and always-correct.
  useEffect(() => {
    const unsub = councilWsClient.subscribe(sessionId, (event: PeerEventWire) => {
      // ws_connected catches missed events fired while we were disconnected
      // (the bus has no replay; without this, lab injects whose broadcast
      // hit "no active connections" leave us forever stale).
      if (
        event.event_type === 'proposal_queued' ||
        event.event_type === 'proposal_resolved' ||
        (event.event_type as unknown as string) === 'ws_connected'
      ) {
        void refresh();
      }
    });
    return unsub;
  }, [sessionId, refresh]);

  // Belt-and-suspenders: also refresh on a window-level event fired by
  // local-page actions that mutate proposals (e.g., the lab inject
  // chip). The WS path is the primary signal but on 2026-05-17 Alan
  // saw the lab chip succeed in the DB while the chip stayed at 0
  // until F5 — likely a missed WS event during the broadcast race.
  // Local dispatch via CustomEvent guarantees a refresh whenever
  // same-page code knows the proposal queue mutated, no matter what
  // the WS layer did. Other callers (chat thread, modal) can do
  // `window.dispatchEvent(new CustomEvent('council:proposals-changed',
  // { detail: { sessionId } }))` to opt in.
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ sessionId?: string }>).detail;
      if (!detail || detail.sessionId === sessionId) void refresh();
    };
    window.addEventListener('council:proposals-changed', handler);
    return () => window.removeEventListener('council:proposals-changed', handler);
  }, [sessionId, refresh]);

  const resolve = useCallback(
    async (proposalId: string, decision: 'approve' | 'dismiss', note?: string) => {
      const payload: ResolveProposalInput = { decision, ...(note ? { note } : {}) };
      try {
        await councilApiClient.resolveProposal(sessionId, proposalId, payload);
        // Refresh after success so the resolved row drops out of the
        // pending list immediately even if the WS event lags.
        await refresh();
      } catch (e) {
        setError(e as Error);
        // Refresh on raced 409 so the operator sees the updated state.
        await refresh();
        throw e;
      }
    },
    [sessionId, refresh],
  );

  return { proposals, loading, error, resolve, refresh };
}
